// measurementValueParts.ts

/**
 * Split a measurement as it is printed ("4.8 cm") into the number the clinician may
 * change and the unit they may not.
 *
 * Nikhil, 1 Sep: "Did you manage to do the non editing of the units part? Which piccolo
 * has". In the cart software the unit is fixed furniture and only the figure is typed.
 * Here the whole cell was editable, so a mistyped edit could leave "4.8 xm", "4.8", or
 * "4.8 cm cm" in a report.
 *
 * That is not only untidy. Every consumer of these values reads them back out of the
 * document text: `parseChainValue` for the chain, the wording rules for the grade,
 * `deriveEchoValues` for anything indexed. A unit that has been mangled or dropped is a
 * value those readers can misread or miss, and the damage shows up two fields away from
 * the edit rather than where it was made.
 *
 * The unit is therefore never put in front of the clinician to edit. The popup receives
 * the number alone and the unit is reattached on commit, unchanged.
 *
 * DELIBERATELY CONSERVATIVE. Anything that is not clearly "<number><rest>" comes back as
 * a number with no unit, which reproduces exactly the old whole-string behaviour. A value
 * this parser does not recognise stays editable rather than becoming uneditable in a way
 * nobody can undo from the UI.
 */

/**
 * Shown where a field has no value yet. Clicking it opens the editor, exactly as a real
 * value does — mirrors `NUMERIC_EMPTY_MARKER` on the stress slots.
 *
 * Lives here rather than beside the backfill that writes it, because the popup has to
 * recognise it too: it is a placeholder, not a figure, so the clinician must be given an
 * empty box to type into rather than the characters "--" to delete first.
 */
export const CHAIN_EMPTY_MARKER = '--';

export type ValueParts = {
  /** What the clinician types into: the figure, without the unit. */
  number: string;
  /** Fixed text reattached on commit. Empty when the value carries no unit. */
  unit: string;
  /**
   * Exactly the characters that sat between the two, reused verbatim on commit.
   *
   * Not cosmetic. If the join normalised this, an untouched value would come back
   * DIFFERENT from the one on the page — "45%" as "45 %", or a `&nbsp;` (U+00A0, which
   * the report HTML is full of) as an ASCII space. `handleCommitChainField` treats an
   * unchanged value as a no-op, so a normalising join would defeat that check: opening a
   * field, typing nothing and pressing Enter would rewrite the value, run the whole chain
   * below it, and dirty the report for autosave. Found by round-tripping every value shape
   * the report prints.
   */
  separator: string;
};

/**
 * Leading number, then whatever follows.
 *
 * Covers the forms the report actually prints: "4.8 cm", "45 ml/m²", "150", "1.5",
 * "-2.3 %", "62 mmHg", ".5 cm". The number may be signed and may omit the integer part;
 * the unit is everything after it, with the separating space removed.
 *
 * Note there is no whitespace requirement between the two — "45%" is as valid as "45 %",
 * and both appear in these reports.
 */
const VALUE_WITH_UNIT = /^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(\s*)(.*)$/;

/** The empty marker followed by a unit. A negative number starts "-2", never "--". */
const EMPTY_WITH_UNIT = new RegExp(`^${CHAIN_EMPTY_MARKER}(\\s*)(.+)$`);

/** Split a printed value into its editable figure, its separator, and its fixed unit. */
export function splitValueAndUnit(display: unknown): ValueParts {
  const text = String(display ?? '').trim();
  if (!text || text === CHAIN_EMPTY_MARKER) {
    return { number: '', unit: '', separator: '' };
  }

  // A placeholder that carries its unit — "-- mmHg". The prose prints the unit as part of
  // the sentence, so the marked value has to own it or a computed figure written back
  // lands in front of the unit already there ("28 mmHg mmHg").
  const blank = EMPTY_WITH_UNIT.exec(text);
  if (blank) {
    const blankUnit = blank[2].trim();
    return { number: '', unit: blankUnit, separator: blankUnit ? blank[1] : '' };
  }

  const found = VALUE_WITH_UNIT.exec(text);
  if (!found) {
    // No leading number at all — the empty marker "--", or a value shape this parser
    // does not know. Hand the whole thing back as editable text, as before.
    return { number: text, unit: '', separator: '' };
  }

  const unit = found[3].trim();
  return { number: found[1], unit, separator: unit ? found[2] : '' };
}

/**
 * Put a typed figure back together with the unit it was separated from.
 *
 * `separator` should be the one `splitValueAndUnit` returned, so an untouched value
 * rebuilds byte-for-byte — see the note on `ValueParts.separator`. It defaults to a single
 * space for callers building a value from scratch.
 *
 * Returns the figure alone when there is no unit, so a ratio does not gain a trailing
 * space. If the clinician has typed the unit back in themselves — easily done, and the
 * value would otherwise read "4.8 cm cm" — the typed text is taken as complete.
 */
/**
 * The unit to reattach when the clinician commits.
 *
 * A printed value carries its own ("4.8 cm" → "cm"), and that one is used verbatim so an
 * untouched value rebuilds byte-for-byte. A placeholder carries none, so the field's
 * canonical unit is used instead — otherwise the first value typed into an empty cell is
 * written bare, and "EF Biplane: 30" is not the same statement as "30 %".
 */
export function resolveUnit(
  parts: ValueParts,
  fieldUnit: string | undefined
): { unit: string; separator: string } {
  if (parts.unit) {
    return { unit: parts.unit, separator: parts.separator };
  }
  return { unit: fieldUnit || '', separator: ' ' };
}

export function joinValueAndUnit(numberText: string, unit: string, separator = ' '): string {
  const value = numberText.trim();
  if (!unit) {
    return value;
  }
  if (!value) {
    return '';
  }
  if (value.toLowerCase().endsWith(unit.toLowerCase())) {
    return value;
  }
  return `${value}${separator}${unit}`;
}

/**
 * Is this draft a complete number, safe to commit?
 *
 * A measurement edit does not just write text — it drives `recomputeChain`, so a
 * half-typed figure propagates as a real one. Typing "4.7" passes through "4" and "4."
 * on the way, and committing either would write wrong indexed values.
 *
 *   "4"     -> true      "4."    -> false (still typing the decimals)
 *   "4.7"   -> true      ""      -> false
 *   ".7"    -> true      "4.7.8" -> false
 */
export function isCompleteNumber(draft: unknown): boolean {
  if (typeof draft !== 'string') {
    return false;
  }
  return /^\d*\.?\d+$/.test(draft.trim());
}
